import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Modal, InputNumber, Button, Tag, Popconfirm, Input } from 'antd';
import { SearchOutlined, DollarOutlined, UndoOutlined, WhatsAppOutlined } from '@ant-design/icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import config from '../../config';
import { NdoePage, NdoePageHeader, NdoeLoading, NdoeError, NdoeResponsiveTable, NdoeDataCard } from '../../components/ndoe';
import { subscribeRealtime } from '../../realtime';
import './centreCreances.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const DEVISE = '$';
const PLAFOND_DEFAUT = 350; // credit_plafond_usd (défaut) — l'admin l'ajuste dans les réglages
const MOYENS = ['Espèces', 'Mobile Money', 'Virement', 'Autre'];

// Statut de créance -> couleur + libellé (dérivé côté API : statut_creance).
const STATUT = {
  soldee: { c: 'green', t: 'Soldée' },
  avoir: { c: 'cyan', t: 'Avoir' },
  non_echue: { c: 'blue', t: 'Non échue' },
  echeance_proche: { c: 'gold', t: 'Échéance proche' },
  echue: { c: 'orange', t: 'Échue' },
  en_retard: { c: 'volcano', t: 'En retard' },
  peu_sure: { c: 'red', t: 'Peu sûre' },
  irrecouvrable: { c: 'magenta', t: 'Irrécouvrable' },
  en_cours: { c: 'default', t: 'En cours' },
};
const money = (n) => `${Number(n || 0).toLocaleString('fr-FR')} ${DEVISE}`;
const codeCommande = (id) => `CMD-${String(id ?? '').padStart(5, '0')}`;
// Solde dû (jamais négatif) et avoir (crédit en faveur de la cliente) d'une dette.
// L'API renvoie `avoir` ; on garde un repli calculé pour les réponses anciennes.
const soldeDe = (d) => Math.max(0, Number(d.montant_convenu || 0) - Number(d.montant_paye || 0));
const avoirDe = (d) => (d.avoir != null ? Number(d.avoir) : Math.max(0, Number(d.montant_paye || 0) - Number(d.montant_convenu || 0)));

/*
 * LA LISTE DES DÉBITRICES — ce que la page montre AVANT qu'on ait choisi.
 * Triée par reste dû décroissant : la question qu'on vient poser ici est
 * « qui dois-je relancer en premier », pas « comment s'appelle-t-elle déjà ».
 */
const ListeCreances = ({ lignes, total, chargement, recherche, onRecherche, onOuvrir, totaux }) => {
  const colonnes = [
    {
      title: 'Cliente', dataIndex: 'nom', key: 'nom', width: 220,
      render: (nom, r) => (
        <span className="cc-liste__cli">
          <b>{nom || '—'}</b>
          {r.telephone && <small>{r.telephone}</small>}
        </span>
      ),
    },
    {
      title: 'Reste dû', dataIndex: 'montant_restant', key: 'restant', width: 130,
      // Tri NUMÉRIQUE : mysql2 rend les SUM en chaînes, et « 1015 » < « 225 »
      // en comparaison de texte — la plus grosse créance se serait retrouvée
      // en bas de liste.
      sorter: (a, b) => (Number(a.montant_restant) || 0) - (Number(b.montant_restant) || 0),
      defaultSortOrder: 'descend',
      render: (v) => <b className={`cc-liste__du ${Number(v) > 0 ? 'est-du' : ''}`}>{money(v)}</b>,
    },
    {
      title: 'Statut', dataIndex: 'statut', key: 'statut', width: 120,
      render: (s) => <Tag color={s === 'Débiteur' ? 'volcano' : s === 'Avoir' ? 'cyan' : 'green'}>{s || '—'}</Tag>,
    },
    {
      title: 'Dettes', dataIndex: 'nbre_dette', key: 'nbre', width: 100,
      render: (n) => <span className="num">{n}</span>,
    },
    {
      title: 'Convenu', dataIndex: 'montant_convenuV', key: 'convenu', width: 130,
      render: (v) => <span className="num">{money(v)}</span>,
    },
    {
      title: 'Payé', dataIndex: 'montant_payeV', key: 'paye', width: 130,
      render: (v) => <span className="num">{money(v)}</span>,
    },
  ];

  return (
    <div className="cc-liste">
      <div className="cc-liste__barre">
        <Input
          allowClear size="large" prefix={<SearchOutlined />}
          placeholder="Filtrer la liste : nom ou téléphone…"
          value={recherche} onChange={(e) => onRecherche(e.target.value)}
          style={{ maxWidth: 380 }}
        />
        <div className="cc-liste__totaux">
          <span><b>{totaux.debitrices}</b> débitrice{totaux.debitrices > 1 ? 's' : ''}</span>
          <span>· reste dû <b>{money(totaux.restant)}</b></span>
          {lignes.length !== total && <span>· {lignes.length} sur {total} affichée(s)</span>}
        </div>
      </div>

      <NdoeResponsiveTable
        data={lignes}
        columns={colonnes}
        rowKey={(r) => r.id_client}
        loading={chargement}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'] }}
        // Toute la ligne ouvre le profil : sur une liste qu'on parcourt, viser
        // un lien étroit est une friction inutile.
        onRow={(r) => ({ onClick: () => onOuvrir(r.id_client), style: { cursor: 'pointer' } })}
        onCardClick={(r) => onOuvrir(r.id_client)}
        cardTitreCol="nom"
        empty={{ icon: '💰', title: 'Aucune créance', text: 'Personne ne doit d\'argent sur cette sélection.' }}
      />
    </div>
  );
};

const CentreCreances = () => {
  const role = useSelector((state) => state.user?.currentUser?.role);
  const isAdmin = role === 'admin';

  const [clients, setClients] = useState([]);
  const [curClient, setCurClient] = useState(null);
  const [dettes, setDettes] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [erreur, setErreur] = useState(null);

  const [enc, setEnc] = useState(null);      // dette en cours d'encaissement
  const [montant, setMontant] = useState(null);
  const [moyen, setMoyen] = useState(MOYENS[0]);
  const [busy, setBusy] = useState(false);

  // LA LISTE D'ABORD (31/07/2026) — demande du propriétaire : « je devrais
  // avoir aussi une liste que je consulte, puis lorsque je clique j'ai les
  // détails ».
  //
  // L'écran s'ouvrait sur un vide et un sélecteur : pour savoir QUI doit de
  // l'argent, il fallait déjà le savoir. On ne pouvait pas parcourir, comparer,
  // repérer la plus grosse créance — trois choses qu'on vient précisément faire
  // ici. La liste des débitrices, triée par reste dû, est maintenant la page.
  const [liste, setListe] = useState([]);
  const [loadingListe, setLoadingListe] = useState(true);
  const [rechercheListe, setRechercheListe] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${DOMAIN}/api/client`);
        setClients(Array.isArray(data) ? data : []);
      } catch (e) { toast.error("Chargement des clientes impossible."); }
      finally { setLoadingClients(false); }
    })();
  }, []);

  const chargerListe = async () => {
    setLoadingListe(true);
    try {
      const { data } = await axios.get(`${DOMAIN}/api/vente/vente/dette`);
      setListe(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Chargement des créances impossible.');
      setListe([]);
    } finally { setLoadingListe(false); }
  };
  useEffect(() => { chargerListe(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const chargerClient = async (id) => {
    setCurClient(id);
    setDettes([]); setPaiements([]);
    if (!id) return;
    setLoading(true); setErreur(null);
    try {
      const [d, p] = await Promise.all([
        axios.get(`${DOMAIN}/api/vente/vente/detteOne?id_client=${id}`),
        axios.get(`${DOMAIN}/api/vente/vente/paiement?id_client=${id}`),
      ]);
      setDettes(Array.isArray(d.data) ? d.data : []);
      const pays = Array.isArray(p.data) ? p.data.filter((x) => String(x.id_client) === String(id)) : [];
      setPaiements(pays);
    } catch (e) {
      setErreur(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const recharger = () => chargerClient(curClient);

  // Temps réel (R2 créances) : quand un AUTRE poste encaisse/annule un paiement
  // (ou franchit le plafond) pour la cliente actuellement affichée, on rafraîchit
  // son profil en direct. Refs pour lire le contexte le plus récent dans le handler.
  const curClientRef = useRef(null); curClientRef.current = curClient;
  const rechargerRef = useRef(null); rechargerRef.current = recharger;
  useEffect(() => {
    const REL = new Set(['PAIEMENT_ENCAISSE', 'PAIEMENT_ANNULE', 'CREDIT_PLAFOND_DEPASSE', 'REMBOURSEMENT', 'RETOUR_CLIENT']);
    let debounce = null;
    const unsub = subscribeRealtime((payload) => {
      const cur = curClientRef.current;
      if (!cur) return;
      const touche = (payload?.notifications || []).some(
        (n) => REL.has(n.action) && n.id_client != null && String(n.id_client) === String(cur),
      );
      if (!touche) return;
      clearTimeout(debounce);
      debounce = setTimeout(() => { if (rechargerRef.current) rechargerRef.current(); }, 700);
    });
    return () => { clearTimeout(debounce); unsub(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // La liste filtrée + ses totaux. Le filtre porte sur le nom ET le téléphone :
  // on cherche une cliente aussi souvent par son numéro que par son nom.
  const listeFiltree = useMemo(() => {
    const n = (v) => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const q = n(rechercheListe).trim();
    const chiffres = rechercheListe.replace(/\D/g, '');
    if (!q) return liste;
    return liste.filter((r) => n(r.nom).includes(q)
      || (chiffres && String(r.telephone ?? '').replace(/\D/g, '').includes(chiffres)));
  }, [liste, rechercheListe]);

  const totauxListe = useMemo(() => ({
    restant: listeFiltree.reduce((s, r) => s + (Number(r.montant_restant) || 0), 0),
    debitrices: listeFiltree.filter((r) => (Number(r.montant_restant) || 0) > 0).length,
  }), [listeFiltree]);

  // --- Agrégats du profil financier ---
  const resume = useMemo(() => {
    const totalDu = dettes.reduce((s, d) => s + Number(d.montant_convenu || 0), 0);
    const totalPaye = dettes.reduce((s, d) => s + Number(d.montant_paye || 0), 0);
    const soldeTotal = dettes.reduce((s, d) => s + soldeDe(d), 0);
    // Avoir total = crédits en faveur de la cliente (trop-perçus). Clampé par dette
    // AVANT la somme (comme le solde) : un avoir ne compense pas une autre dette.
    const avoirTotal = dettes.reduce((s, d) => s + avoirDe(d), 0);
    const impayees = dettes.filter((d) => Number(d.montant_convenu || 0) > Number(d.montant_paye || 0));
    const plusVieille = impayees
      .map((d) => d.date_echeance).filter(Boolean).sort()[0] || null;
    const pire = ['irrecouvrable', 'peu_sure', 'en_retard', 'echue', 'echeance_proche', 'non_echue']
      .find((s) => impayees.some((d) => d.statut_creance === s)) || null;
    let risque = { c: 'green', t: 'Bon payeur' };
    if (pire === 'irrecouvrable' || pire === 'peu_sure') risque = { c: 'red', t: 'Peu sûre' };
    else if (pire === 'en_retard' || pire === 'echue') risque = { c: 'orange', t: 'En retard' };
    else if (pire === 'echeance_proche') risque = { c: 'gold', t: 'À surveiller' };
    if (soldeTotal > PLAFOND_DEFAUT) risque = { c: 'volcano', t: 'Plafond dépassé' };
    return { totalDu, totalPaye, soldeTotal, avoirTotal, plusVieille, risque, nbImpayees: impayees.length };
  }, [dettes]);

  const client = clients.find((c) => String(c.id) === String(curClient));

  // --- Encaissement / Remboursement (même modale, deux modes) ---
  const ouvrirEncaisser = (d) => {
    const solde = soldeDe(d);
    setEnc({ ...d, mode: 'encaisser', plafond: solde });
    setMontant(solde || null);
    setMoyen(MOYENS[0]);
  };
  const ouvrirRembourser = (d) => {
    const avoir = avoirDe(d);
    setEnc({ ...d, mode: 'rembourser', plafond: avoir });
    setMontant(avoir || null);
    setMoyen(MOYENS[0]);
  };
  const valider = async () => {
    if (!enc || !montant || montant <= 0) return;
    if (montant > enc.plafond) {
      toast.error(enc.mode === 'rembourser'
        ? `Le montant dépasse l'avoir disponible (${money(enc.plafond)}).`
        : `Le montant dépasse le solde dû (${money(enc.plafond)}).`);
      return;
    }
    setBusy(true);
    try {
      if (enc.mode === 'rembourser') {
        await axios.post(`${DOMAIN}/api/vente/vente/remboursement`, {
          id_commande: enc.id_commande, montant, moyen, id_client: curClient,
        });
        toast.success('Remboursement enregistré (avoir résorbé).');
      } else {
        await axios.post(`${DOMAIN}/api/vente/vente/paiement`, {
          id_commande: enc.id_commande, montant, moyen, id_client: curClient,
        });
        toast.success('Paiement enregistré.');
      }
      setEnc(null);
      await recharger();
    } catch (e) {
      toast.error(e.response?.data?.error || "Opération refusée.");
    } finally { setBusy(false); }
  };

  const annulerPaiement = async (p) => {
    try {
      await axios.delete(`${DOMAIN}/api/vente/vente/paiement/${p.id_paiement}`);
      toast.success('Paiement annulé (dette recréditée).');
      await recharger();
    } catch (e) {
      toast.error(e.response?.data?.error || "Annulation refusée.");
    }
  };

  const waLink = (tel) => {
    const n = String(tel || '').replace(/\D/g, '');
    if (!n) return null;
    const full = n.startsWith('243') ? n : `243${n.replace(/^0/, '')}`;
    return `https://wa.me/${full}`;
  };

  const detteCols = [
    { title: 'Commande', dataIndex: 'id_commande', render: (v) => <span className="cc-code">{codeCommande(v)}</span> },
    { title: 'Dû', dataIndex: 'montant_convenu', align: 'right', render: money },
    { title: 'Payé', dataIndex: 'montant_paye', align: 'right', render: money },
    { title: 'Solde', key: 'solde', align: 'right',
      render: (_, d) => {
        const av = avoirDe(d);
        return av > 0
          ? <b className="cc-avoir-amt">+{money(av)}</b>
          : <b>{money(soldeDe(d))}</b>;
      } },
    { title: 'Échéance', dataIndex: 'date_echeance', render: (v) => (v ? format(new Date(v), 'dd/MM/yyyy') : '—') },
    { title: 'Statut', key: 'st', render: (_, d) => {
      const s = STATUT[d.statut_creance] || STATUT.en_cours;
      const suffix = d.statut_creance === 'avoir' && avoirDe(d) > 0
        ? ` · ${money(avoirDe(d))}`
        : (d.jours_retard > 0 ? ` · ${d.jours_retard}j` : '');
      return <Tag color={s.c}>{s.t}{suffix}</Tag>;
    } },
    { title: '', key: 'act', align: 'right', render: (_, d) => {
      if (soldeDe(d) > 0) {
        return <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => ouvrirEncaisser(d)}>Encaisser</Button>;
      }
      if (avoirDe(d) > 0) {
        // Avoir (crédit cliente) : remboursement cash réservé à l'admin (creances.rembourser).
        return isAdmin
          ? <Button size="small" icon={<UndoOutlined />} onClick={() => ouvrirRembourser(d)}>Rembourser</Button>
          : <Tag color="cyan">Avoir</Tag>;
      }
      return <Tag color="green">Réglée</Tag>;
    } },
  ];

  const paieCols = [
    { title: 'Date', dataIndex: 'created_at', render: (v) => (v ? format(new Date(v), 'dd/MM/yyyy HH:mm') : '—') },
    { title: 'Commande', dataIndex: 'id_commande', render: (v) => (v ? codeCommande(v) : '—') },
    { title: 'Montant', dataIndex: 'montant', align: 'right',
      render: (v) => <span style={{ color: Number(v) < 0 ? '#c0392b' : undefined }}>{money(v)}</span> },
    { title: 'Moyen', dataIndex: 'moyen', render: (v) => v || '—' },
    { title: 'Statut', key: 'st', render: (_, p) => {
      if (p.annule_le) return <Tag color="default">Annulé</Tag>;
      if (Number(p.montant) < 0) {
        const estRemb = String(p.moyen || '').toUpperCase() === 'REMBOURSEMENT'
          || /rembours/i.test(String(p.reference || ''));
        return estRemb ? <Tag color="cyan">Remboursement</Tag> : <Tag color="volcano">Contre-passation</Tag>;
      }
      return <Tag color="green">Encaissé</Tag>;
    } },
    { title: '', key: 'act', align: 'right', render: (_, p) => (
      isAdmin && !p.annule_le && Number(p.montant) > 0 && p.id_dette ? (
        <Popconfirm title="Annuler ce paiement ? La dette sera recréditée." okText="Annuler le paiement" cancelText="Non"
          onConfirm={() => annulerPaiement(p)}>
          <Button size="small" danger icon={<UndoOutlined />}>Annuler</Button>
        </Popconfirm>
      ) : null) },
  ];

  // --- Rendus MOBILE (cartes) : mêmes actions que les colonnes, sans table
  // compressée à défilement horizontal (pattern design system NdoeResponsiveTable).
  const renderDetteCard = (d) => {
    const av = avoirDe(d);
    const solde = soldeDe(d);
    const s = STATUT[d.statut_creance] || STATUT.en_cours;
    const suffix = d.statut_creance === 'avoir' && av > 0 ? ` ${money(av)}`
      : (d.jours_retard > 0 ? ` · ${d.jours_retard}j` : '');
    let action = <Tag color="green">Réglée</Tag>;
    if (solde > 0) action = <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => ouvrirEncaisser(d)}>Encaisser</Button>;
    else if (av > 0) action = isAdmin
      ? <Button size="small" icon={<UndoOutlined />} onClick={() => ouvrirRembourser(d)}>Rembourser</Button>
      : <Tag color="cyan">Avoir</Tag>;
    return (
      <NdoeDataCard
        title={codeCommande(d.id_commande)}
        subtitle={d.date_echeance ? `Échéance ${format(new Date(d.date_echeance), 'dd/MM/yyyy')}` : undefined}
        badge={<Tag color={s.c}>{s.t}{suffix}</Tag>}
        metrics={[
          { label: 'Dû', value: money(d.montant_convenu) },
          { label: 'Payé', value: money(d.montant_paye) },
          av > 0 ? { label: 'Avoir', value: `+${money(av)}` } : { label: 'Solde', value: money(solde) },
        ]}
        actions={action}
      />
    );
  };

  const renderPaieCard = (p) => {
    let badge = <Tag color="green">Encaissé</Tag>;
    if (p.annule_le) badge = <Tag color="default">Annulé</Tag>;
    else if (Number(p.montant) < 0) {
      const estRemb = String(p.moyen || '').toUpperCase() === 'REMBOURSEMENT' || /rembours/i.test(String(p.reference || ''));
      badge = estRemb ? <Tag color="cyan">Remboursement</Tag> : <Tag color="volcano">Contre-passation</Tag>;
    }
    const action = isAdmin && !p.annule_le && Number(p.montant) > 0 && p.id_dette ? (
      <Popconfirm title="Annuler ce paiement ? La dette sera recréditée." okText="Annuler le paiement" cancelText="Non"
        onConfirm={() => annulerPaiement(p)}>
        <Button size="small" danger icon={<UndoOutlined />}>Annuler</Button>
      </Popconfirm>
    ) : null;
    return (
      <NdoeDataCard
        title={p.id_commande ? codeCommande(p.id_commande) : 'Paiement'}
        subtitle={p.created_at ? format(new Date(p.created_at), 'dd/MM/yyyy HH:mm') : undefined}
        badge={badge}
        metrics={[
          { label: 'Montant', value: money(p.montant) },
          { label: 'Moyen', value: p.moyen || '—' },
        ]}
        actions={action}
      />
    );
  };

  return (
    <NdoePage className="cc-page">
      <NdoePageHeader
        eyebrow="Crédit & créances"
        title="Centre des créances"
        subtitle={curClient ? 'Dettes, encaissements et profil financier de la cliente.' : 'Qui doit de l’argent, et combien. Cliquez une ligne pour ouvrir son profil et encaisser.'}
      />

      <div className="cc-bar">
        {/* Le chemin du retour. Sans lui, on entre dans un profil et on ne
            revient à la liste qu'en rechargeant la page. */}
        {curClient && (
          <button type="button" className="cc-retour" onClick={() => { chargerClient(null); chargerListe(); }}>
            ← Toutes les créances
          </button>
        )}
        <Select
          style={{ minWidth: 280 }} size="large" placeholder="Rechercher une cliente…"
          value={curClient} onChange={chargerClient} showSearch optionFilterProp="label"
          loading={loadingClients} allowClear suffixIcon={<SearchOutlined />}
          options={clients.map((c) => ({ value: c.id, label: `${c.nom}${c.telephone ? ' · ' + c.telephone : ''}` }))}
        />
        {client && waLink(client.telephone) && (
          <a className="cc-wa" href={waLink(client.telephone)} target="_blank" rel="noreferrer">
            <WhatsAppOutlined /> Relancer sur WhatsApp
          </a>
        )}
      </div>

      {!curClient ? (
        <ListeCreances
          lignes={listeFiltree}
          total={liste.length}
          chargement={loadingListe}
          recherche={rechercheListe}
          onRecherche={setRechercheListe}
          onOuvrir={chargerClient}
          totaux={totauxListe}
        />
      ) : loading ? (
        <NdoeLoading rows={6} />
      ) : erreur ? (
        <NdoeError text={erreur} onRetry={recharger} />
      ) : (
        <>
          <div className="cc-stats">
            <div className="cc-stat"><span className="l">Total dû</span><span className="v">{money(resume.totalDu)}</span></div>
            <div className="cc-stat"><span className="l">Total payé</span><span className="v">{money(resume.totalPaye)}</span></div>
            <div className="cc-stat s-solde"><span className="l">Solde restant</span><span className="v">{money(resume.soldeTotal)}</span></div>
            {resume.avoirTotal > 0 && (
              <div className="cc-stat s-avoir"><span className="l">Avoir (crédit cliente)</span><span className="v">{money(resume.avoirTotal)}</span></div>
            )}
            <div className="cc-stat"><span className="l">Profil</span><span className="v"><Tag color={resume.risque.c}>{resume.risque.t}</Tag></span>
              <span className="sub">{resume.nbImpayees} créance(s) · plafond {money(PLAFOND_DEFAUT)}</span></div>
          </div>

          <h3 className="cc-h3">Dettes</h3>
          <NdoeResponsiveTable
            rowKey="id_dette" columns={detteCols} data={dettes} renderCard={renderDetteCard}
            pagination={false} scroll={{ x: 720 }}
            empty={{ icon: '💳', title: 'Aucune dette', text: 'Aucune dette pour cette cliente.' }}
          />

          <h3 className="cc-h3">Historique des paiements</h3>
          <NdoeResponsiveTable
            rowKey="id_paiement" columns={paieCols} data={paiements} renderCard={renderPaieCard}
            pagination={false} scroll={{ x: 640 }}
            empty={{ icon: '🧾', title: 'Aucun paiement', text: 'Aucun paiement enregistré.' }}
          />
        </>
      )}

      <Modal
        open={!!enc} onCancel={() => setEnc(null)} onOk={valider} confirmLoading={busy}
        okText={enc?.mode === 'rembourser' ? 'Enregistrer le remboursement' : 'Enregistrer le paiement'}
        cancelText="Annuler"
        title={enc?.mode === 'rembourser' ? 'Rembourser un avoir' : 'Encaisser un paiement'}
        okButtonProps={{ disabled: !montant || montant <= 0 }}
      >
        {enc && (
          <div className="cc-enc">
            {enc.mode === 'rembourser' ? (
              <p>Commande <b>{codeCommande(enc.id_commande)}</b> — avoir disponible : <b>{money(enc.plafond)}</b></p>
            ) : (
              <p>Commande <b>{codeCommande(enc.id_commande)}</b> — solde dû : <b>{money(enc.plafond)}</b></p>
            )}
            <label>{enc.mode === 'rembourser' ? 'Montant remboursé' : 'Montant encaissé'}</label>
            {/* inputMode="decimal" : InputNumber ne pose NI type NI inputMode sur
                son <input> (rc-input-number/lib/InputNumber.js:486) — le navigateur
                retombe donc sur type=text et monte le clavier AZERTY. Ce champ a en
                plus autoFocus : la secrétaire ouvrait chaque encaissement de sa
                journée sur le clavier des lettres. La prop atteint bien le DOM
                (inputProps y est étalé, l.493).
                RÈGLE : tout InputNumber de montant ou de quantité porte
                inputMode="decimal". Sans effet sur desktop. */}
            <InputNumber inputMode="decimal" style={{ width: '100%' }} min={1} max={enc.plafond} value={montant}
              onChange={setMontant} addonAfter={DEVISE} autoFocus />
            <label style={{ marginTop: 12 }}>{enc.mode === 'rembourser' ? 'Moyen de remboursement' : 'Moyen de paiement'}</label>
            <Select style={{ width: '100%' }} value={moyen} onChange={setMoyen}
              options={MOYENS.map((m) => ({ value: m, label: m }))} />
          </div>
        )}
      </Modal>
    </NdoePage>
  );
};

export default CentreCreances;
